/* ===========================================================================
   LSC - Complementary pairs: add to cart behaviour
   ---------------------------------------------------------------------------
   Defines <lsc-complementary-quotes>, the custom element wrapping the card
   grid. Uses the theme's own AJAX cart handshake (see assets/product-bundle
   .js): dispatch cart:bundled-sections, POST to cart_add_url, refetch
   /cart.js, publish cartUpdate, dispatch ajaxProduct:added.

   LSC - 2026-10-18 audit P2: was 6.6KB of inline <script> in
   snippets/lsc-cq-script.liquid, re-sent and re-parsed on every product page
   view. As an asset it is fetched and parsed once and then cached. It carries
   no per-section values, so nothing had to change to move it.

   Defined once per document, guarded by customElements.get - which is why
   nothing in here may reference a per-instance class name.
   =========================================================================== */
(function () {
  if (customElements.get('lsc-complementary-quotes')) return;

  class LscComplementaryQuotes extends HTMLElement {
    connectedCallback() {
      this.openDrawer = this.dataset.openDrawer === 'true';
      this.addedDuration = parseInt(this.dataset.addedDuration, 10) || 2000;

      // LSC - 2026-10-18 audit P8: one delegated listener on the element
      // rather than one per button. Besides being cheaper, this keeps working
      // if the card markup is ever replaced by a section re-render, where
      // per-button listeners would be lost with the nodes they were bound to.
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('.js-lscq-atc');
        if (btn && this.contains(btn)) this.onAdd(event, btn);
      });
    }

    get cartDrawer() {
      return document.querySelector('cart-drawer');
    }

    // LSC - 2026-10-18: scope lookups to the enclosing card, not to the
    // button's immediate parent.
    //
    // These used to read btn.parentElement, which worked only because the full
    // width button and its error/status paragraphs were all direct children of
    // the <article>. The round icon button is wrapped in a slot div (inside the
    // quote grid, or right-aligned on its own line) while those paragraphs
    // deliberately stay at card width - so parentElement found neither, and in
    // icon mode an add-to-cart failure would have been silent and the screen
    // reader confirmation would never have fired.
    //
    // closest('article') rather than a card class: the custom element is
    // defined once for the whole document, so it must not carry a selector
    // belonging to one section instance.
    cardEl(btn) {
      return btn.closest('article') || btn.parentElement;
    }

    errorEl(btn) {
      const scope = this.cardEl(btn);
      return scope && scope.querySelector('.js-lscq-error');
    }

    showError(btn, message) {
      const el = this.errorEl(btn);
      if (!el) return;
      el.textContent = message || '';
      el.toggleAttribute('hidden', !message);
    }

    // LSC - 2026-10-12 product audit #31: the card's polite live region.
    statusEl(btn) {
      const scope = this.cardEl(btn);
      return scope && scope.querySelector('.js-lscq-status');
    }

    // LSC - 2026-10-12 product audit #31: announce "<product> added to cart".
    // The region is cleared first and filled ~100ms later so a repeat add of
    // the same product is announced again (identical text is otherwise not
    // re-read), then cleared after a few seconds so the stale message is not
    // found later when browsing the page. setTimeout rather than
    // requestAnimationFrame, which does not fire in a background tab.
    announceAdded(btn) {
      const el = this.statusEl(btn);
      if (!el) return;
      window.clearTimeout(btn._lscqStatusTimer);
      window.clearTimeout(btn._lscqStatusFill);
      el.textContent = '';
      btn._lscqStatusFill = window.setTimeout(() => {
        el.textContent = btn.dataset.addedMessage || '';
      }, 100);
      btn._lscqStatusTimer = window.setTimeout(() => {
        el.textContent = '';
      }, Math.max(this.addedDuration, 4000));
    }

    flashAdded(btn) {
      window.clearTimeout(btn._lscqTimer);
      btn.classList.add('is-added');
      this.announceAdded(btn);
      btn._lscqTimer = window.setTimeout(() => {
        btn.classList.remove('is-added');
      }, this.addedDuration);
    }

    // LSC - 2026-10-18 audit P8: cart URLs now fall back through
    // Shopify.routes.root, which the storefront always defines and which
    // carries the locale/market prefix. The old fallback was a bare
    // '/cart/add', which would have posted to the wrong path on a localized
    // URL if window.theme were ever missing.
    routes() {
      const themeRoutes = (window.theme && window.theme.routes) || {};
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return {
        add: themeRoutes.cart_add_url || root + 'cart/add',
        cart: themeRoutes.cart_url || root + 'cart',
      };
    }

    async onAdd(event, btn) {
      event.preventDefault();
      if (btn.hasAttribute('aria-busy')) return;

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const { add: addUrl, cart: cartUrl } = this.routes();

      this.showError(btn, '');

      if (window.theme && window.theme.settings && window.theme.settings.cartType === 'page') {
        const form = document.createElement('form');
        form.method = 'post';
        form.action = addUrl;
        form.style.display = 'none';
        form.innerHTML =
          '<input type="hidden" name="id" value="' + variantId + '">' +
          '<input type="hidden" name="quantity" value="1">';
        document.body.appendChild(form);
        form.submit();
        return;
      }

      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('aria-disabled', 'true');

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sectionsToBundle,
            sections_url: window.location.pathname,
          }),
        });

        const parsedState = await response.json();

        if (parsedState.status) {
          this.showError(btn, parsedState.description || parsedState.message);
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:error', {
              detail: { errorMessage: parsedState.description },
            })
          );
          return;
        }

        this.flashAdded(btn);

        // LSC - 2026-10-18 audit P8: the second round trip is DELIBERATE, not
        // an oversight. /cart/add returns the added line and the bundled
        // section HTML, not the whole cart, and the theme's cartUpdate
        // subscribers (drawer, count bubble, free-shipping bar) expect a full
        // cart object. This mirrors the theme's own product-bundle.js. Do not
        // "optimize" it away: the cart drawer stops updating if you do.
        const cartJson = await (await fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })).json();
        cartJson.sections = parsedState.sections;

        if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'lsc-complementary-quotes',
            cart: cartJson,
          });
        }

        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } })
        );

        if (this.openDrawer && this.cartDrawer && typeof this.cartDrawer.show === 'function') {
          this.cartDrawer.show(btn);
        }
      } catch (error) {
        console.error(error);
        this.showError(btn, 'Sorry, something went wrong. Please try again.');
      } finally {
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define('lsc-complementary-quotes', LscComplementaryQuotes);
})();